export type CountdownUnit = "days" | "hours" | "minutes" | "seconds";
export type CountdownParts = Record<CountdownUnit, number>;
export interface CountdownCopy {
	expired: string;
	units: Record<CountdownUnit, string>;
	/** Full words for the boxed layout's tile labels; `units` stays the terse accessible/text form. */
	unitWords: Record<CountdownUnit, string>;
}
export interface CountdownDisplay {
	text: string;
	units: {unit: CountdownUnit; digits: number[]; suffix: string; word: string}[];
}

const MS_PER_SECOND = 1_000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const UNITS: readonly CountdownUnit[] = ["days", "hours", "minutes", "seconds"];

/** A fractional final second stays visible until the deadline, never expiring a live link early. */
export function countdownParts(remainingMs: number): CountdownParts {
	const seconds = Number.isFinite(remainingMs) ? Math.max(0, Math.ceil(remainingMs / MS_PER_SECOND)) : 0;
	return {
		days: Math.floor(seconds / SECONDS_PER_DAY),
		hours: Math.floor(seconds % SECONDS_PER_DAY / SECONDS_PER_HOUR),
		minutes: Math.floor(seconds % SECONDS_PER_HOUR / SECONDS_PER_MINUTE),
		seconds: seconds % SECONDS_PER_MINUTE,
	};
}

/** The digits and accessible name share one padded representation and the same localized suffixes. */
export function countdownDisplay(remainingMs: number, copy: CountdownCopy): CountdownDisplay {
	const parts = countdownParts(remainingMs);
	const first = UNITS.findIndex(unit => parts[unit] > 0);
	if (first === -1) return {text: copy.expired, units: []};
	const units = UNITS.slice(first).map(unit => ({
		unit,
		digits: Array.from(String(parts[unit]).padStart(unit === "days" ? 1 : 2, "0"), Number),
		suffix: copy.units[unit],
		word: copy.unitWords[unit],
	}));
	return {text: units.map(({digits, suffix}) => `${digits.join("")}${suffix}`).join(" "), units};
}

/** Delay to the next deadline-aligned second; null means the clock should stop. */
export function countdownTickDelay(remainingMs: number): number | null {
	if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;
	return remainingMs % MS_PER_SECOND || MS_PER_SECOND;
}
